/**
 * PDT Warning Banner component (T147).
 *
 * Displays a warning banner when the user is at or near the PDT limit.
 * This banner is designed to be shown on the Trading page.
 */
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Info, XCircle } from 'lucide-react';
import { complianceService } from '@/services/compliance';

interface PDTWarningBannerProps {
  /**
   * Whether to show the banner even when not at limit.
   * Default is false (only show when at or near limit).
   */
  showAlways?: boolean;
}

export function PDTWarningBanner({ showAlways = false }: PDTWarningBannerProps) {
  const { data: pdtStatus, isLoading } = useQuery({
    queryKey: ['compliance', 'pdt-status'],
    queryFn: () => complianceService.getPDTStatus(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Don't show anything while loading
  if (isLoading) {
    return null;
  }

  // Don't show if no data
  if (!pdtStatus) {
    return null;
  }

  // Determine if we should show the banner
  const shouldShow = showAlways || pdtStatus.isAtLimit || pdtStatus.remaining <= 1;

  if (!shouldShow) {
    return null;
  }

  // Determine banner style and content based on status
  const getBannerStyle = () => {
    if (pdtStatus.isAtLimit) {
      return {
        bgColor: 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800',
        textColor: 'text-red-800 dark:text-red-200',
        iconColor: 'text-red-500',
        Icon: XCircle,
      };
    }
    if (pdtStatus.remaining === 1) {
      return {
        bgColor: 'bg-yellow-50 dark:bg-yellow-950 border-yellow-200 dark:border-yellow-800',
        textColor: 'text-yellow-800 dark:text-yellow-200',
        iconColor: 'text-yellow-500',
        Icon: AlertTriangle,
      };
    }
    return {
      bgColor: 'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800',
      textColor: 'text-blue-800 dark:text-blue-200',
      iconColor: 'text-blue-500',
      Icon: Info,
    };
  };

  const getTitle = () => {
    if (pdtStatus.isAtLimit) {
      return 'PDT Limit Reached';
    }
    if (pdtStatus.remaining === 1) {
      return 'PDT Warning';
    }
    return 'Day Trade Status';
  };

  const getMessage = () => {
    if (pdtStatus.isAtLimit) {
      return (
        <>
          You have made <strong>{pdtStatus.dayTradesCount} day trades</strong> in the last 5
          business days, reaching the limit. Any additional day trade may flag your account as a
          Pattern Day Trader (PDT), which requires a minimum account balance of $25,000.
        </>
      );
    }
    if (pdtStatus.remaining === 1) {
      return (
        <>
          You have <strong>1 day trade remaining</strong> in the current rolling 5-day window. Make
          sure this trade counts before you use it.
        </>
      );
    }
    return (
      <>
        You have used{' '}
        <strong>
          {pdtStatus.dayTradesCount} of {pdtStatus.dayTradesLimit}
        </strong>{' '}
        day trades in the rolling 5-day window. <strong>{pdtStatus.remaining}</strong> remaining.
      </>
    );
  };

  const style = getBannerStyle();
  const { Icon } = style;

  return (
    <div className={`rounded-lg border p-4 ${style.bgColor}`}>
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${style.iconColor}`} />
        <div className={style.textColor}>
          <h4 className="font-medium text-sm mb-1">{getTitle()}</h4>
          <p className="text-sm opacity-90">{getMessage()}</p>

          {pdtStatus.isAtLimit && (
            <div className="mt-2 text-xs opacity-75">
              <strong>What is a day trade?</strong> Buying and selling (or selling short and
              covering) the same security on the same trading day.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PDTWarningBanner;
